import { useCallback, useMemo } from 'react'
import { useUpdateColumnVisibility } from '../requests/useUpdateColumnVisibility'
import { useUserSettings } from '../useUserSettings'
import { ColumnDef } from '@tanstack/react-table'

export const useColumnVisibility = <TData>(
  columns: ColumnDef<TData>[],
  columnType: 'dashboard' | 'customer'
) => {
  const { updateColumnVisibility } = useUpdateColumnVisibility(columnType)
  const { getDashboardColumns, getCustomerColumns } = useUserSettings()

  // derive visible columns from settings so we can watch the actual array value
  const savedColumns = useMemo(() => {
    return columnType === 'dashboard' ? getDashboardColumns() : getCustomerColumns()
  }, [getDashboardColumns, getCustomerColumns])

  const initialColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {}
    const visibleColumns = savedColumns

    columns.forEach((column) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const columnId = (column as any).id || (column as any).accessorKey
      if (columnId) {
        const isAlwaysVisible = columnId === 'state' || columnId === 'edit'
        const isSavedVisible = visibleColumns ? visibleColumns.includes(columnId) : true
        visibility[columnId] = isAlwaysVisible || isSavedVisible
      }
    })
    return visibility
  }, [savedColumns, columns])

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      const visibleColumns = Object.entries(visibility)
        .filter(([_, isVisible]) => isVisible)
        .map(([field]) => field)

      if (!visibleColumns.includes('state') && columnType === 'dashboard') {
        visibleColumns.push('state')
      }
      if (!visibleColumns.includes('edit')) {
        visibleColumns.push('edit')
      }

      updateColumnVisibility(visibleColumns)
    },
    [updateColumnVisibility]
  )

  return {
    initialColumnVisibility,
    handleColumnVisibilityChange,
  }
}
